/* eslint-disable */
import { faCheckCircle, faChevronLeft, faCircleXmark, faPhone, faPhoneAlt, faUpload } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'

import MainTitle from '../../components/MainTitle'
import ClientNav from '../../components/ClientNav'
import useAuth from '../../hooks/useAuth'
import moment from 'moment'
import { ImageConfig } from '../../components/ImageConfig'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import Modal from '../../components/Modal'

const GpSession = ({
  clientData = {},
  claimData = {},
  sessionData = {},
}) => {
  const navigate = useNavigate()

  const { clientId, claimId, sessionId } = useParams()
  const { auth, setShowLoader, createInfo } = useAuth()

  const axios = useAxiosPrivate()
  const [prescriptionIssued, setPrescriptionIssued] = useState(sessionData?.additional_data?.prescription_issued ?? null)
  const [activeTab, setActiveTab] = useState("patient");
  const referrableServices = claimData?.metadata?.referrable_services ?? []
  const [notes, setNotes] = useState(sessionData?.notes ?? '')
  const [fileList, setFileList] = useState([])
  const [savedFiles, setSavedFiles] = useState([])
  const wrapperRef = useRef(null)
  const [errorMsg, setErrorMsg] = useState(false)
  const [modalTitle, setModalTitle] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [completionReasons, setCompletionReasons] = useState([])
  const [selectedCompletionReason, setSelectedCompletionReason] = useState('0')
  const [servicesReferred, setServicesReferred] = useState(sessionData?.additional_data?.services_referred ?? []);
  const completed = sessionData?.confirmation_reason_id

  const referredValid = useRef(true)
  const prescriptionValid = useRef(true)

  const onDragEnter = () => wrapperRef.current.classList.add('dragover')
  const onDragLeave = () => wrapperRef.current.classList.remove('dragover')
  const onDragOver = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }
  const onDrop = (e) => {
    e.preventDefault()
    wrapperRef.current.classList.remove('dragover')
    onFileDrop(e.nativeEvent.dataTransfer.files)
  }

  useEffect(() => {
    fetchSavedFiles()
    getCompletionReasons()
  }, [])

  const resetModal = () => {
    setShowModal(false)
    setModalTitle('')
  }

  const fetchSavedFiles = async () => {
    try {
      const response = await axios.post(`/sessions/gp/fetch-documents/${sessionId}`);
      setSavedFiles(response?.data?.result)
    } catch {
      // createInfo('error', 'Failed to fetch docs')
    }
  }

  const goToClaim = () => {
    navigate(`/clients/${clientId}/claims/${claimId}`)
  }

  const fileRemove = (file) => {
    const updatedList = [...fileList]
    updatedList.splice(fileList.indexOf(file), 1)
    setFileList(updatedList)
  }

  const savedFileRemove = async (fileId) => {
    try {
      setShowLoader(true)
      const response = await axios.get(`/sessions/gp/delete-document/${fileId}`);
      if (response?.data?.error) {
        createInfo('error', 'Failed to delete document')
        return
      } else {
        setSavedFiles(savedFiles.filter(f => f.id !== fileId))
        createInfo('success', 'Successfully deleted document')
      }
    } catch {
      createInfo('error', 'Failed to delete document')
    } finally {
      setShowLoader(false)
    }
  }

  const allowedFileTypes = [
    '.doc',
    '.docx',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.xls',
    '.xlsx',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    '.csv',
    'application/pdf',
    '.pdf',
    'image/jpeg',
    '.jpg',
    'image/png',
    '.png',
    'image/tiff',
    '.tiff',
    '.txt'
  ]

  const isValidFileType = (file) => {
    const extension = `.${file.name.split('.').pop()}`
    const mimeType = file.type
    return (
      allowedFileTypes.includes(extension) ||
      allowedFileTypes.includes(mimeType)
    )
  }

  const onFileUpload = async (e) => {
    e.preventDefault()
    const newFile = e.target.files[0]

    if (newFile && isValidFileType(newFile)) {
      const updatedList = [...fileList, newFile]
      setFileList(updatedList)
    } else {
      console.error('Invalid file type. Please select a valid file.')
    }
  }

  const onFileDrop = async (files) => {
    const newFiles = Array.from(files)

    if (newFiles.length > 0 && newFiles.every(isValidFileType)) {
      setFileList((prevList) => [...prevList, ...newFiles])
    } else {
      createInfo('error', 'Invalid file type')
    }
  }

  const validateFiles = async () => {
    try {
      const response = await axios.get('/clients/max-file-size')
      const MAX_FILE_SIZE = response?.data?.result?.find(i => i.key==='max_filesize')?.value // KB

      if (!fileList.length) {
        setErrorMsg('Please choose a file')
        return
      }

      fileList.forEach(file => {
        if (!isValidFileType(file)) {
          setErrorMsg(`Invalid file type: ${file.name}`)
          createInfo('error', `Invalid file type: ${file.name}`)
          return
        }

        const fileSizeKiloBytes = file.size / 1024

        if (fileSizeKiloBytes > MAX_FILE_SIZE) {
          setErrorMsg(
            `File "${file.name}" exceeds the maximum file size limit`
          )
          createInfo('error', `File "${file.name}" exceeds the size limit`)
          return
        }
        setErrorMsg('')
      })
    } catch (error) {
      setErrorMsg('Failed to validate files. Please try again.')
    }
  }

  const uploadDocuments = async () => {
    setShowLoader(true)
    validateFiles()
    const fd = new FormData()
    for (let i = 0; i < fileList.length; i++) {
      fd.append('shared-document-' + i, fileList[i], fileList[i].name)
    }
    try {
      const response = await axios.post(`/sessions/gp/upload-documents/${sessionId}`, fd)

      if (response?.data?.error) {
        createInfo(
          'error',
          'Failed to upload all ocuments'
        )

        if (response?.data?.result) {
          const uploadedFiles = response?.data?.result?.map(f => f.name);
          setFileList(fileList.filter(f => !uploadedFiles?.includes(f.name)))
          setSavedFiles([...savedFiles, ...response?.data?.result]) 
        }
        return
      }

      setFileList([])
      setSavedFiles([...savedFiles, ...response?.data?.result])

      createInfo(
        'success',
        'Uploaded ' + response.data.result.length + ' document(s)'
      )
    } catch (error) {
      createInfo(
        'error',
        'Failed to upload documents'
      )
    } finally {
      setShowLoader(false)
    }
  }

  const CompleteSessionElement = ({ sessionData }) => {
    const now = moment()
    const sessionEndTime = moment(sessionData?.sessionData?.end_time)
    const timeBeforeEnd = sessionEndTime.subtract(15, 'minutes')

    const afterCompletionPeriod = now.isAfter(timeBeforeEnd)
    const completeSession = async (e) => {
      e.preventDefault()
      setShowLoader(true)
      try {
        const completionReason = JSON.parse(selectedCompletionReason)

        const response = await axios.post(`/sessions/complete-gp-session/${sessionId}`, {
          confirmation_reason_id: completionReason.id,
          notes: notes?.trim(),
          additional_data: {
            services_referred: servicesReferred,
            prescription_issued: prescriptionIssued
          }
        })
        if (!response.data.error) {
          createInfo('success', `Session has been updated`)
          setTimeout(() => {
            window.location.reload()
          }, 1500);
        }
      } catch (error) {
        console.error('Error completing session:', error)
      } finally {
        setShowLoader(false)
      }
    }

    return (
      <form onSubmit={completeSession}>
        <div className="flex justify-center mb-4 p-2">
          <h3 className="text-lg font-bold">
            Are you sure you want to complete this appointment?
          </h3>
        </div>
        <div className="mb-3">
          <label
            htmlFor="name"
            className="block font-medium text-gray-600 text-sm"
          >
            Please select a reason for completing this appointment
          </label>
          <div className="mt-1 relative rounded-md">
            <select
              defaultValue={selectedCompletionReason || '0'}
              className="px-2 py-2 block w-full border border-gray-300 rounded-md shadow-md"
              required
              id="confirmation-reason"
              onChange={(e) => setSelectedCompletionReason(e.target.value)}
            >
              <option disabled value="0">
                Select a completion reason
              </option>
              {completionReasons
                ?.filter((reason) => {
                  if (reason.title === 'Completed' && !afterCompletionPeriod) {
                    return false
                  }
                  return true
                })
                .map((reason) => (
                  <option key={reason.id} value={JSON.stringify(reason)}>
                    {reason.title}
                  </option>
                ))}
            </select>
          </div>
          <p className="text-sm text-red-500 italic mb- mt-2">
            Once the appointment is completed, you will be unable to make any
            changes.
          </p>
        </div>
        <div className="flex mt-2 w-full">
          <button
            type="submit"
            className={` mt-4 mr-4 w-1/2 ${!selectedCompletionReason || selectedCompletionReason === '0'
                ? 'cursor-not-allowed text-sm inline-flex justify-center items-center bg-gray-500 border-gray-500 text-white font-bold py-2.5 px-4 border rounded'
                : 'btn blue'
              }`}
            disabled={
              !selectedCompletionReason || selectedCompletionReason === '0'
            }
          >
            Yes, complete
          </button>

          <button
            type="button"
            className="btn red mt-4 w-1/2"
            onClick={resetModal}
          >
            No
          </button>
        </div>
      </form>
    )
  }

  const getCompletionReasons = async () => {
    try {
      setShowLoader(true)
      const response = await axios.get(`/completion-reasons/all`)
      if (response.data.result) {
        await setCompletionReasons(response.data.result.reasons)
      }
    } catch (error) { }
    setShowLoader(false)
  }
  
  const completeSessionClicked = () => {

    prescriptionValid.current = prescriptionIssued !== null

    if (!referredValid.current || !prescriptionValid.current) {
      createInfo('error', 'Please ensure all information complete');
      return
    }

    setModalTitle(`Complete Appointment`)
    setShowModal(true)
  }

  return (
    <div>
      {showModal && (
        <Modal title={modalTitle} body={<CompleteSessionElement sessionData={sessionData} />} show={resetModal} />
      )}
      <MainTitle
        title={`Client: ${clientData.name} (${clientData.reference})`}
        client={clientData}
        claim={claimData}
        backButton
        child={clientData.child}
      />

      {clientData.id &&
        <ClientNav
          active="claims"
          client={clientData}
          audit={sessionData.id}
          type={'Session'}
          id={clientId}
        />
      }

      {sessionData.id && (
        <section>
          <span
            className="inline-flex items-center text-slate-500 mb-3 cursor-pointer hover:text-slate-600"
            onClick={goToClaim}
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
            <span className="text-sm ml-2">View Claim</span>
          </span>
          {claimData?.metadata?.dependant_details && (
            <div className="bg-amber-100 border-l-4 border-amber-500 text-amber-700 p-2 px-4 text-md mb-2 rounded flex justify-between items-center">
              <p className="font-semibold">
                This appointment was booked for a dependant
              </p>
            </div>
          )}

          <section className="w-full bg-white rounded-xl shadow-md py-4 px-4 mb-8">
            <h2
              className="font-bold text-lg mb-4"
            >
              {claimData.external_reference} - {claimData.service.title}
            </h2>


            <div className="mb-5 ml-0.5 border-b border-gray-200">
              <nav className="flex gap-8" aria-label="Details tabs">
                <button
                  type="button"
                  onClick={() => setActiveTab("patient")}
                  className={`border-b-2 px-1 pb-3 text-base font-semibold transition-colors ${activeTab === "patient"
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    }`}
                >
                  Patient
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab("session")}
                  className={`border-b-2 px-1 pb-3 text-base font-semibold transition-colors ${activeTab === "session"
                    ? "border-blue-600 text-blue-700"
                    : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                    }`}
                >
                  Session
                </button>
              </nav>
            </div>

            {activeTab === 'patient' && (
              <>
                <div className="space-y-5">
                  <p className="text-xl font-semibold text-gray-900">
                    Patient Information: {claimData?.metadata?.dependant_details ? claimData?.metadata?.dependant_details?.name : clientData?.name}
                  </p>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-3 md:items-stretch">
                    {/* Client Address */}
                    <div className="h-full rounded-xl border border-gray-200 bg-gray-50/50 px-5 pt-4 pb-3">
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">
                        Patient Address
                      </h3>

                      <div className="text-base leading-normal text-gray-600">
                        <p className="m-0">
                          {claimData?.metadata?.client_address?.addressLine1}
                        </p>

                        {claimData?.metadata?.client_address?.addressLine2 && (
                          <p className="m-0">
                            {claimData.metadata.client_address.addressLine2}
                          </p>
                        )}

                        <p className="m-0">
                          {claimData?.metadata?.client_address?.city}
                        </p>

                        <p className="m-0">
                          {claimData?.metadata?.client_address?.county}
                        </p>

                        <p className="m-0">
                          {claimData?.metadata?.client_address?.postcode}
                        </p>
                      </div>
                    </div>

                    {/* GP Details */}
                    <div className="h-full rounded-xl border border-gray-200 bg-gray-50/50 px-5 pt-4 pb-3">
                      <h3 className="mb-3 text-lg font-semibold text-gray-900">
                        GP Details
                      </h3>

                      <div className="text-base leading-normal text-gray-600">
                        <p className="m-0 font-semibold text-gray-900">
                          {claimData?.metadata?.gp_details?.name || "Not provided"}
                        </p>

                        <p className="m-0">
                          {claimData?.metadata?.gp_details?.address}
                        </p>

                        <p className="m-0">
                          {claimData?.metadata?.gp_details?.city}
                        </p>

                        <p className="m-0">
                          {claimData?.metadata?.gp_details?.postcode}
                        </p>

                        {claimData?.metadata?.gp_details?.phone && (
                          <div className="mt-2 flex items-center gap-2">
                            <FontAwesomeIcon
                              icon={faPhoneAlt}
                              className="text-sm"
                            />
                            <p className="m-0">
                              {claimData.metadata.gp_details.phone}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div className="grid h-full grid-rows-2 gap-3">
                      {/* Sex Assigned at Birth */}
                      <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                        <h3 className="mb-2 text-lg font-semibold text-gray-900">
                          Sex assigned at Birth
                        </h3>

                        <p className="m-0 text-base leading-normal text-gray-600">
                          {(
                            claimData?.metadata?.dependant_details?.sex_at_birth ||
                            claimData?.metadata?.sex_at_birth
                          )
                            ? (
                              claimData?.metadata?.dependant_details?.sex_at_birth ||
                              claimData?.metadata?.sex_at_birth
                            ).charAt(0).toUpperCase() +
                            (
                              claimData?.metadata?.dependant_details?.sex_at_birth ||
                              claimData?.metadata?.sex_at_birth
                            ).slice(1)
                            : "Not provided"}
                        </p>
                      </div>

                      {/* Date of Birth and Child */}
                      <div className="grid min-h-0 grid-cols-2 gap-3">
                        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                          <h3 className="mb-2 text-lg font-semibold text-gray-900">
                            Date of Birth
                          </h3>

                          <p className="m-0 text-base leading-normal text-gray-600">
                            {claimData?.metadata?.dependant_details?.date_of_birth ||
                              clientData?.client_details?.date_of_birth
                              ? moment(
                                claimData?.metadata?.dependant_details?.date_of_birth ||
                                clientData?.client_details?.date_of_birth
                              ).format("DD/MM/YYYY")
                              : "Not provided"}
                          </p>
                        </div>

                        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
                          <h3 className="mb-2 text-lg font-semibold text-gray-900">
                            Under 18
                          </h3>

                          <p className="m-0 text-base leading-normal text-gray-600">
                            {(claimData?.metadata?.dependant_details?.date_of_birth ||
                              clientData?.client_details?.date_of_birth) &&
                              moment().diff(
                                moment(
                                  claimData?.metadata?.dependant_details?.date_of_birth ||
                                  clientData?.client_details?.date_of_birth
                                ),
                                "years"
                              ) < 18
                              ? "Yes"
                              : "No"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>


                </div>
                <div className='flex-1 flex gap-5 mb-2'>
                  <div className="flex-1 rounded-xl border mt-5 border-gray-200 bg-gray-50/50 p-5">
                    <h3 className="mb-3 text-lg font-semibold text-gray-900">
                      Existing Medical Conditions
                    </h3>

                    {claimData?.metadata?.medical_conditions?.length > 0 ? (
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 list-disc pl-5 text-base ">
                        {claimData.metadata.medical_conditions.map((condition, index) => (
                          <li key={index}>
                            {condition
                              .replace(/_/g, " ")
                              .replace(/^./, (char) => char.toUpperCase())}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-base ">None provided</p>
                    )}
                  </div>
                  <div className="flex-1 rounded-xl border mt-5 border-gray-200 bg-gray-50/50 p-5">
                    <h3 className="mb-3 text-lg font-semibold text-gray-900">
                      Additional Notes
                    </h3>

                    <p className="whitespace-pre-wrap break-words text-base leading-relaxed">
                      {claimData?.metadata?.additional_notes || "None provided"}
                    </p>
                  </div>
                </div>
              </>
            )}
            {activeTab === 'session' && (
              <div className="space-y-3">
                <p className="text-xl font-semibold text-gray-900 pb-2">
                  Session Information
                </p>
                <div className="grid w-full grid-cols-1 items-start gap-6 lg:grid-cols-5">
                  {/* Left Section: Form Fields */}
                  <div className="min-w-0 space-y-5 lg:col-span-2">
                    <div className="space-y-2">
                      <p className="text-base font-semibold text-gray-900">
                        Has a prescription been issued?{" "}
                        <span className="text-red-500">*</span>
                      </p>

                      <div className="flex w-full gap-3">

                        <label
                          className={`flex flex-1 items-center gap-2 rounded-lg border px-4 py-3 text-base shadow-sm transition ${completed
                              ? "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400 opacity-75"
                              : "cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:bg-blue-50"
                            }`}
                        >
                          <input
                            type="radio"
                            name="prescriptionIssued"
                            value="yes"
                            checked={prescriptionIssued === true}
                            onChange={() => setPrescriptionIssued(true)}
                            className="accent-blue-600 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed"
                            disabled={completed}
                          />
                          <span>Yes</span>
                        </label>


                        <label
                          className={`flex flex-1 items-center gap-2 rounded-lg border px-4 py-3 text-base shadow-sm transition ${completed
                              ? "cursor-not-allowed border-gray-200 text-gray-400 opacity-75"
                              : "cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:bg-blue-50"
                            }`}
                        >
                          <input
                            type="radio"
                            name="prescriptionIssued"
                            value="yes"
                            checked={prescriptionIssued === false}
                            onChange={() => setPrescriptionIssued(true)}
                            className="accent-blue-600 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed"
                            disabled={completed}
                          />
                          <span>No</span>
                        </label>
                      </div>

                      {!prescriptionValid.current && (
                        <p className="text-xs text-red-500">
                          Please select whether a prescription has been issued.
                        </p>
                      )}
                    </div>

                    {/* Service Referred */}
                    <div className="space-y-2">
                      <label
                        htmlFor="serviceReferred"
                        className="block text-base font-semibold text-gray-900"
                      >
                        Services Referred
                      </label>
                      <div className="w-1/3">
                        {referrableServices.map((service, index) => {
                          return (
                            <div key={index} className="flex gap-2 w-full justify-between">
                              <label
                                htmlFor={'checkbox' + index}
                              >
                                {service}
                              </label>
                              <input
                                id={'checkbox' + index}
                                type='checkbox'
                                checked={servicesReferred?.includes(service)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setServicesReferred([...servicesReferred, service])
                                  } else {
                                    setServicesReferred(servicesReferred.filter(s => s !== service))
                                  }
                                }}
                                disabled={completed}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                      <label
                        htmlFor="notes"
                        className={`block text-base font-semibold ${completed ? "text-gray-400" : "text-gray-900"
                          }`}
                      >
                        Notes
                      </label>

                      <textarea
                        id="notes"
                        name="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={5}
                        placeholder="Enter any additional notes here..."
                        disabled={completed}
                        className={`w-full rounded-lg border px-4 py-3 text-base shadow-sm transition focus:outline-none ${completed
                            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-500 placeholder:text-gray-400"
                            : "border-gray-300 bg-white text-gray-700 placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                          }`}
                      />
                    </div>
                  </div>

                  {/* Right Section: Upload Documents */}
                  <div className="min-w-0 self-start lg:col-span-3">
                    <section className="flex w-full min-w-0 flex-col bg-white">
                      {/* Heading and Save Button */}
                      <div className="mb-4 mt-[-4px] flex justify-between gap-3">
                        <h2 className="text-lg font-bold text-gray-900">
                          Upload{completed ? 'ed' : ''} Documents
                        </h2>

                        {!completed && (
                          <button
                            type="button"
                            onClick={uploadDocuments}
                            className="flex shrink-0 mt-[-16px] items-center rounded-lg border border-blue-500 bg-blue-500 px-4 py-2 text-white transition hover:bg-white hover:text-blue-500"
                          >
                            <FontAwesomeIcon icon={faUpload} />
                            <span className="pl-2">Save</span>
                          </button>
                        )}
                      </div>


                      {/* Drag and Drop Area */}
                      {!completed && (
                        <div
                          ref={wrapperRef}
                          onDragEnter={onDragEnter}
                          onDragLeave={onDragLeave}
                          onDragOver={onDragOver}
                          onDrop={onDrop}
                          className="w-full"
                        >
                          <label
                            htmlFor="file-upload"
                            className="flex h-[180px] w-full mt-[-8px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-4 py-4 text-center transition hover:border-blue-500 hover:bg-blue-50 sm:px-6"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="1.5"
                              stroke="currentColor"
                              className="h-9 w-9 shrink-0 text-gray-400"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 16V4m0 0L8 8m4-4l4 4M4 16v3a1 1 0 001 1h14a1 1 0 001-1v-3"
                              />
                            </svg>

                            <p className="mt-2 break-words text-sm font-semibold text-blue-600 sm:text-base">
                              Drag and drop files here or click to browse
                            </p>

                            <p className="mt-1 text-sm text-gray-500">
                              Upload your supporting documents
                            </p>

                            <p className="mt-1 text-xs leading-5 text-gray-500">
                              CSV, XLS, XLSX, PNG, JPG, PDF, DOC or DOCX
                              <span className="block">
                                Maximum 10 MB per file
                              </span>
                            </p>

                            <input
                              id="file-upload"
                              name="file-upload"
                              type="file"
                              accept=".doc,.docx,.xls,.xlsx,.csv,.pdf,.png,.jpg,.jpeg"
                              multiple
                              className="sr-only"
                              onChange={onFileUpload}
                            />

                            {errorMsg && (
                              <p className="mt-1 text-sm text-red-600">
                                {errorMsg}
                              </p>
                            )}
                          </label>
                        </div>
                      )}

                      {/* Selected Files */}
                      {fileList.length > 0 && (
                        <div className="mt-5">
                          <h3 className="mb-3 text-sm font-semibold text-gray-900">
                            Selected files ({fileList.length})
                          </h3>

                          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                            {fileList.map((item, index) => (
                              <div
                                key={`${item.name}-${index}`}
                                className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
                              >
                                <div className="flex min-w-0 items-center gap-3">
                                  <img
                                    src={
                                      ImageConfig[item.type.split("/")[1]] ||
                                      ImageConfig.default
                                    }
                                    alt=""
                                    className="h-10 w-10 shrink-0 object-contain"
                                  />

                                  <div className="min-w-0">
                                    <p className="break-words text-sm font-medium text-gray-800">
                                      {item.name}
                                    </p>

                                    <p className="text-xs text-gray-500">
                                      {typeof item.size === "number"
                                        ? `${(item.size / 1024).toFixed(1)} KB`
                                        : item.size}
                                    </p>
                                  </div>
                                </div>

                                {!completed && (
                                  <button
                                    type="button"
                                    aria-label={`Remove ${item.name}`}
                                    onClick={() => fileRemove(item)}
                                    className="shrink-0 text-gray-500 transition hover:text-red-600"
                                  >
                                    <FontAwesomeIcon
                                      icon={faCircleXmark}
                                      className="h-5 w-5"
                                    />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Uploaded Files */}
                      {savedFiles.length > 0 && (
                        <div className={!completed ? 'mt-5' : 'mt-[-8px]'}>
                          {!completed && (
                            <h3 className="mb-3 text-sm font-semibold text-gray-900">
                              Uploaded files ({savedFiles.length})
                            </h3>
                          )}

                          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                            {savedFiles.map((item, index) => (
                              <div
                                key={`${item.name}-${index}`}
                                className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
                              >
                                <div className="flex min-w-0 items-center gap-3">
                                  <img
                                    src={
                                      ImageConfig.default
                                    }
                                    alt=""
                                    className="h-10 w-10 shrink-0 object-contain"
                                  />

                                  <div className="min-w-0">
                                    <p className="break-words text-sm font-medium text-gray-800">
                                      {item.name}
                                    </p>

                                    <p className="text-xs text-gray-500">
                                      {typeof item.size === "number"
                                        ? `${(item.size / 1024).toFixed(1)} KB`
                                        : item.size}
                                    </p>
                                  </div>
                                </div>

                                {!completed && (
                                  <button
                                    type="button"
                                    aria-label={`Remove ${item.name}`}
                                    onClick={() => savedFileRemove(item.id)}
                                    className="shrink-0 text-gray-500 transition hover:text-red-600"
                                  >
                                    <FontAwesomeIcon
                                      icon={faCircleXmark}
                                      className="h-5 w-5"
                                    />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </section>
                  </div>
                </div>
                {!completed && (
                  <div className="toggle flex items-center flex-1">
                    <button
                      className="btn warning !text-[16px]"
                      onClick={(e) => completeSessionClicked()}
                    >
                      Complete Appointment
                    </button>
                  </div>
                )}
              </div>
            )}

          </section>
        </section>
      )}
    </div>
  )
}

export default GpSession
